import { useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../store";
import { selectIsAuthenticated } from "../store/slices/authSlice";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import { useLazyStaffStoreAppDetailsQuery } from "../api";
import { getAppKey } from "../shared/lib/helpers";
import { convertServicesToEnvironment } from "../shared/lib/environment";
import { updateGlobalAPIBaseURLs } from "../api/axios/api-instances";
import { refreshAccessToken } from "../api/helpers/refresh-token";

export function ProtectedView() {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const [isLoading, setIsLoading] = useState(true);
  const { storeKey } = useParams();

  const initializeEnvironment = useCallback(
    async (storeKey: string) => {
      const appDetails = await getAppDetails({
        store_key: storeKey,
        app_key: getAppKey(),
      }).unwrap();

      if (appDetails) {
        await refreshAccessToken();

        const services = convertServicesToEnvironment(appDetails.result.services);
        updateGlobalAPIBaseURLs(services);
        setIsLoading(false);
      }
    },
    [getAppDetails]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      setIsLoading(false);
    } else {
      if (storeKey) {
        initializeEnvironment(storeKey);
      } else {
        navigate("/stores");
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, initializeEnvironment, navigate, storeKey]);

  return (
    <>
      {isLoading ? (
        <Flex className="h-dvh" gap="2" justify="center" align="center">
          <Spinner size="3" />
          <Text color="gray" size="2">
            Starting app...
          </Text>
        </Flex>
      ) : (
        <Outlet />
      )}
    </>
  );
}
